import type { AdoptionBreakdownRow, AdoptionDriver, AdoptionDriverKey, AdoptionScope, AdoptionScore, AdoptionTrend } from './AdoptionTypes';

/**
 * Deterministic sample data for the adoption screens, standing in for the
 * bmai-insights adoption endpoint until it lands. Seeded so values are stable
 * across renders (mirrors the SignalTile `sample()` philosophy).
 */

const WEEKS = 12;

/** Small seeded PRNG (mulberry32) so the fixture is deterministic. */
function rng(seed: number): () => number {
    let a = seed >>> 0;
    return () => {
        a |= 0;
        a = (a + 0x6d2b79f5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

function hash(text: string): number {
    let h = 2166136261;
    for (let i = 0; i < text.length; i++) {
        h ^= text.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return h >>> 0;
}

/** An S-curve series climbing to `end` over the window, with a gentle wobble. */
function buildSeries(end: number, climb: number, phase: number): number[] {
    const start = end - climb;
    const out: number[] = [];
    for (let i = 0; i < WEEKS; i++) {
        const t = i / (WEEKS - 1);
        const base = start + (end - start) * (t * t * (3 - 2 * t));
        const wobble = Math.sin(i * 0.9 + phase) * Math.max(1.5, climb * 0.3);
        out.push(Math.round(Math.max(0, Math.min(100, base + wobble))));
    }
    out[WEEKS - 1] = end;
    return out;
}

/** Twelve weekly bucket labels (M/D), oldest → newest, ending at the current week. */
export function weekLabels(): string[] {
    const now = new Date();
    const labels: string[] = [];
    for (let i = WEEKS - 1; i >= 0; i--) {
        const d = new Date(now.getTime() - i * 7 * 24 * 60 * 60 * 1000);
        labels.push(`${d.getMonth() + 1}/${d.getDate()}`);
    }
    return labels;
}

interface DriverSeed {
    key: AdoptionDriverKey;
    label: string;
    description: string;
    weight: number;
    score: number;
    deltaPts: number;
    climb: number;
    phase: number;
    accent: AdoptionDriver['accent'];
}

const DRIVER_SEEDS: DriverSeed[] = [
    { key: 'volume', label: 'Activity', description: 'Meaningful throughput per active user', weight: 20, score: 53, deltaPts: 3, climb: 8, phase: 0.5, accent: 'cyan' },
    { key: 'frequency', label: 'Frequency', description: 'Active days per week (of 5)', weight: 30, score: 73, deltaPts: 6, climb: 14, phase: 2, accent: 'indigo' },
    { key: 'complexity', label: 'Complexity', description: 'Low / Medium / High task mix', weight: 25, score: 60, deltaPts: 0, climb: 3, phase: 4, accent: 'amber' },
    { key: 'consistency', label: 'Consistency', description: 'Share of weeks active (habit)', weight: 25, score: 99, deltaPts: 0, climb: 12, phase: 1, accent: 'rose' },
];

function buildDrivers(scale: number): AdoptionDriver[] {
    return DRIVER_SEEDS.map((s) => {
        const score = Math.round(Math.max(0, Math.min(100, s.score * scale)));
        return {
            key: s.key,
            label: s.label,
            description: s.description,
            weight: s.weight,
            score,
            deltaPts: s.deltaPts,
            weekly: buildSeries(score, s.climb, s.phase),
            accent: s.accent,
        };
    });
}

function composite(drivers: AdoptionDriver[]): number {
    const byKey = new Map(drivers.map((d) => [d.key, d.score]));
    const value = 0.3 * (byKey.get('frequency') ?? 0) + 0.25 * (byKey.get('consistency') ?? 0) + 0.25 * (byKey.get('complexity') ?? 0) + 0.2 * (byKey.get('volume') ?? 0);
    return Math.round(value);
}

export function buildScore(): AdoptionScore {
    const drivers = buildDrivers(1);
    const score = composite(drivers);
    return {
        score,
        deltaPts: 3,
        percentile: 83,
        benchmarkMedian: 58,
        benchmarkTopQuartile: 72,
        labels: weekLabels(),
        weekly: buildSeries(score, 16, 1),
        drivers,
        context: { reach: 0.63, activeUsers: 748, totalUsers: 1180, providers: 4, coverage: 4, coverageTotal: 4 },
    };
}

const DEPARTMENTS = ['Engineering', 'Data', 'Finance', 'Security', 'Carrier Relations', 'Payment Operations'];
const ROLES = ['Software Engineer', 'Data Scientist', 'Product Manager', 'Analyst', 'Support Specialist'];
const ORG_UNITS = ['R&D', 'Corporate', 'Go-To-Market', 'Operations'];
const MANAGERS = ['A. Rivera', 'J. Chen', 'M. Okafor', 'S. Patel'];
const TASKS = ['Code Generation', 'Debugging', 'Data Analysis', 'Summarization', 'Q&A / Research'];
const PROVIDERS = ['Anthropic', 'OpenAI', 'Azure OpenAI', 'Google'];
const MODELS = ['Claude 3.5 Sonnet', 'GPT-4o', 'o1 / reasoning', 'GPT-4o mini', 'Gemini 1.5 Pro'];
const FIRST = ['Ava', 'Noah', 'Mia', 'Liam', 'Emma', 'Owen', 'Isla', 'Ethan', 'Aria', 'Leo', 'Nora', 'Kai'];
const LAST = ['Thompson', 'Kim', 'Garcia', 'Walsh', 'Diaz', 'Reed', 'Novak', 'Okafor', 'Patel', 'Chen', 'Rivera', 'Blake'];

function pick<T>(list: T[], n: number): T {
    return list[n % list.length] as T;
}

/** ~48 active leaf "people" rows (each carrying every groupable dimension) plus a dozen inactive directory identities for the Inactive segment / Idle view. */
export function buildBreakdown(): AdoptionBreakdownRow[] {
    const rows: AdoptionBreakdownRow[] = [];
    const count = 48;
    for (let i = 0; i < count; i++) {
        const r = rng(i + 1);
        const department = pick(DEPARTMENTS, Math.floor(r() * DEPARTMENTS.length));
        const label = `${pick(FIRST, i)} ${pick(LAST, i * 7 + 3)}`;
        const dims: Record<string, string> = {
            department,
            role: pick(ROLES, Math.floor(r() * ROLES.length)),
            orgUnit: pick(ORG_UNITS, Math.floor(r() * ORG_UNITS.length)),
            manager: pick(MANAGERS, Math.floor(r() * MANAGERS.length)),
            task: pick(TASKS, Math.floor(r() * TASKS.length)),
            provider: pick(PROVIDERS, Math.floor(r() * PROVIDERS.length)),
            model: pick(MODELS, Math.floor(r() * MODELS.length)),
        };
        const deptSeed = (hash(department) % 40) + 45;
        const frequency = Math.round(Math.max(10, Math.min(100, deptSeed + (r() - 0.4) * 20)));
        const volume = Math.round(Math.max(5, Math.min(100, deptSeed - 12 + (r() - 0.5) * 24)));
        const complexity = Math.round(Math.max(10, Math.min(100, deptSeed - 6 + (r() - 0.5) * 28)));
        const consistency = Math.round(Math.max(60, Math.min(100, 88 + r() * 12)));
        const adoption = Math.round(0.3 * frequency + 0.25 * consistency + 0.25 * complexity + 0.2 * volume);
        rows.push({
            id: `person-${i}`,
            label,
            dims,
            adoption,
            volume,
            frequency,
            complexity,
            consistency,
            reach: Math.round((0.4 + r() * 0.6) * 100) / 100,
            spend: Math.round(20 + r() * 380),
            inactive: false,
        });
    }
    const inactiveCount = 12;
    for (let i = 0; i < inactiveCount; i++) {
        const r = rng(1000 + i);
        rows.push({
            id: `inactive-${i}`,
            label: `${pick(FIRST, i + 5)} ${pick(LAST, i * 5 + 2)}`,
            dims: {
                department: pick(DEPARTMENTS, Math.floor(r() * DEPARTMENTS.length)),
                role: pick(ROLES, Math.floor(r() * ROLES.length)),
                orgUnit: pick(ORG_UNITS, Math.floor(r() * ORG_UNITS.length)),
                manager: pick(MANAGERS, Math.floor(r() * MANAGERS.length)),
                task: '—',
                provider: '—',
                model: '—',
            },
            adoption: 0,
            volume: 0,
            frequency: 0,
            complexity: 0,
            consistency: 0,
            reach: 0,
            spend: 0,
            inactive: true,
        });
    }
    return rows;
}

/** Scope shifts phase/scale deterministically so scoped trends differ yet stay stable across renders. */
export function buildTrend(scope?: AdoptionScope): AdoptionTrend {
    const seed = scope ? hash(`${scope.dimension}:${scope.value}`) : 0;
    const shift = (seed % 5) - 2;
    const scale = scope ? 0.85 + (seed % 30) / 100 : 1;
    return {
        labels: weekLabels(),
        drivers: DRIVER_SEEDS.map((s) => {
            const score = Math.round(Math.max(0, Math.min(100, s.score * scale)));
            return { key: s.key, score, weekly: buildSeries(score, s.climb, s.phase + shift) };
        }),
    };
}
